import { useEffect, useState } from 'react'
import { registerServiceWorker } from '@/utils/serviceWorkerRegistration'
import { toast } from 'sonner'

/**
 * CACHE-BUST-003: Application Version Tracking
 */
interface AppVersion {
  version: string
  build: string
  commit: string
  lastUpdated: string
  environment: string
}

let initialVersion: AppVersion | null = null

export const useServiceWorker = () => {
  const [isOfflineReady, setIsOfflineReady] = useState(false)
  const [needsUpdate, setNeedsUpdate] = useState(false)
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null)
  const [currentVersion, setCurrentVersion] = useState<AppVersion | null>(null)
  const [isStale, setIsStale] = useState(false)

  useEffect(() => {
    let hasShownOfflineToast = false

    registerServiceWorker({
      onSuccess: reg => {
        console.log('[App] Service worker registered successfully')
        setRegistration(reg)
      },
      onUpdate: reg => {
        console.log('[App] Service worker update available')
        setNeedsUpdate(true)
        setRegistration(reg)

        // Show update notification
        toast.info('New version available', {
          description: 'Click to refresh and update the app',
          action: {
            label: 'Refresh',
            onClick: () => {
              window.location.reload()
            },
          },
          duration: Infinity, // Don't auto-dismiss
        })
      },
      onOfflineReady: () => {
        console.log('[App] Offline mode ready')
        setIsOfflineReady(true)

        // Show offline ready notification (only once)
        if (!hasShownOfflineToast) {
          toast.success('Offline mode enabled', {
            description: 'You can now use the app without internet',
            duration: 5000,
          })
          hasShownOfflineToast = true
        }
      },
    }).catch(error => {
      console.error('[App] Service worker registration failed:', error)
      // NO fallback - app continues normally without service worker
    })
  }, [])

  const updateServiceWorker = async () => {
    if (!registration) {
      console.warn('[App] No registration available for update')
      return
    }

    const newWorker = registration.waiting

    if (newWorker) {
      console.log('[App] Activating new service worker...')

      // Tell the new service worker to skip waiting
      newWorker.postMessage({ type: 'SKIP_WAITING' })

      // Reload page after new worker takes control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[App] New service worker activated, reloading...')
        window.location.reload()
      })
    } else {
      console.warn('[App] No waiting worker available')
    }
  }

  // CACHE-BUST-003: Aggressive Version Checking
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        // Prevent caching of version.json
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        })

        if (!response.ok) {
          if (import.meta.env.DEV) {
            console.warn('[Version Check] Failed to fetch version.json')
          }
          return
        }

        const version: AppVersion = await response.json()

        if (!initialVersion) {
          // First load - store initial version
          initialVersion = version
          setCurrentVersion(version)

          if (import.meta.env.DEV) {
            console.log('[Version Check] Initial version:', version)
          }
        } else {
          // Subsequent check - compare versions
          const buildChanged = initialVersion.build !== version.build
          const versionChanged = initialVersion.version !== version.version

          if (buildChanged || versionChanged) {
            console.warn(
              '[Version Check] New version detected! Force reloading...',
              {
                old: initialVersion,
                new: version,
              }
            )

            setIsStale(true)

            // Show update notification
            toast.warning('New app version available', {
              description: 'Updating to latest version...',
              duration: 3000,
            })

            // Give user 3 seconds to see pending operations
            setTimeout(() => {
              // Clear service worker caches
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker
                  .getRegistrations()
                  .then(registrations => {
                    registrations.forEach(registration => {
                      registration.unregister()
                    })
                  })
              }

              // Clear all caches
              if ('caches' in window) {
                caches.keys().then(names => {
                  names.forEach(name => {
                    caches.delete(name)
                  })
                })
              }

              // Force reload with cache bypass
              window.location.reload()
            }, 3000)
          } else if (import.meta.env.DEV) {
            console.log('[Version Check] Version unchanged:', version.build)
          }
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[Version Check] Error fetching version:', error)
        }
      }
    }

    // Initial fetch
    fetchVersion()

    // Check every 5 minutes
    const checkInterval = setInterval(fetchVersion, 5 * 60 * 1000)

    return () => {
      clearInterval(checkInterval)
    }
  }, [])

  return {
    isOfflineReady,
    needsUpdate,
    updateServiceWorker,
    registration,
    currentVersion,
    isStale,
  }
}
